import React, { useContext, useMemo, useState } from "react";
import Modal from "../components/Modal";
import { DialogWrapper } from "components/containers";
import { TextPlain, TitleH2 } from "components/generic";
import { Button } from "antd";
import { ButtonProps } from "antd/es/button/button";

interface IModalOptions {
  title: string;
  content: string | React.ReactNode;
  confirmButtonText: string;
  confirmButtonProps?: ButtonProps & React.RefAttributes<HTMLElement>;
  cancelButtonText: string;
  onConfirm: () => void;
  onCancel?: () => void;
}

const ContextConfirmModal = React.createContext<{
  show: (options: IModalOptions) => void;
  hide: () => void;
}>({
  show: () => {},
  hide: () => {}
});

export const useConfirmModal = () => {
  return useContext(ContextConfirmModal);
};

export const ConfirmModalProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [modalState, setModalState] = useState<IModalOptions | null>(null);
  const [isLoadingCancel, setIsLoadingCancel] = useState(false);
  const [isLoadingConfirm, setIsLoadingConfirm] = useState(false);

  const modalApi = useMemo(() => {
    return {
      show: (options: IModalOptions) => setModalState(options),
      hide: () => setModalState(null)
    };
  }, [setModalState]);

  return (
    <ContextConfirmModal.Provider value={modalApi}>
      {children}
      {modalState && (
        <Modal
          isShow={true}
          handleClose={async () => {
            await modalState?.onCancel?.();
            modalApi.hide();
          }}
        >
          <DialogWrapper style={{ width: "80vw", maxWidth: "400px" }}>
            <TitleH2>{modalState.title}</TitleH2>
            <TextPlain style={{ wordBreak: "break-word" }}>
              {modalState.content}
            </TextPlain>
            <div className="buttons">
              {modalState.onCancel || modalState.cancelButtonText ? (
                <Button
                  loading={isLoadingCancel}
                  type="default"
                  size="large"
                  danger
                  onClick={async () => {
                    try {
                      setIsLoadingCancel(true);
                      await modalState?.onCancel?.();
                      modalApi.hide();
                    } finally {
                      setIsLoadingCancel(false);
                    }
                  }}
                  className="butCancel"
                >
                  {modalState.cancelButtonText}
                </Button>
              ) : (
                <span />
              )}
              <Button
                type="primary"
                size="large"
                loading={isLoadingConfirm}
                onClick={async () => {
                  try {
                    setIsLoadingConfirm(true);
                    await modalState.onConfirm();
                    modalApi.hide();
                  } finally {
                    setIsLoadingConfirm(false);
                  }
                }}
                {...modalState.confirmButtonProps}
              >
                {modalState.confirmButtonText}
              </Button>
            </div>
          </DialogWrapper>
        </Modal>
      )}
    </ContextConfirmModal.Provider>
  );
};
